import { PresentDeviceUserActions } from '@/app/platform/platform-data/actions/types';
import { Inject, Injectable } from '@angular/core';
import {
  PRESENT_DEVICE_USER_SERVICE,
  PresentDeviceUserFilters,
  PresentDeviceUserService
} from '@/app/platform/platform-data/services/types';
import { AsyncResult } from '@common/result/asyncResult';
import { ErrorInformation } from '@shared/models/error/error-information';
import { PresentDeviceUserStore } from '@/app/platform/platform-data/store/present-device-user.store';
import { PresentDeviceUsersUpdateListener } from '@/app/platform/platform-data/listeners/present-device-users-update-listener.service';
import { Subscription } from 'rxjs';
import { unsubscribeAll } from '@common/utils/rxjs-utils';

@Injectable({
  providedIn: 'root'
})
export class PresentDeviceUserStoreActions implements PresentDeviceUserActions {
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly store: PresentDeviceUserStore,
    private readonly presentUsersUpdateListener: PresentDeviceUsersUpdateListener,
    @Inject(PRESENT_DEVICE_USER_SERVICE)
    private readonly presentDeviceUserService: PresentDeviceUserService
  ) {}

  async startListening(): Promise<void> {
    // Eerst abonneren, dan wachten: stopt het luisteren intussen, dan ruimt stopListening() ze nog op.
    this.subscriptions.push(
      this.presentUsersUpdateListener.update$.subscribe(async () => {
        await this.loadAllFiltered();
      }),
      this.presentUsersUpdateListener.isAvailable$.subscribe(isAvailable => {
        if (!isAvailable) {
          this.store.set([]);
        }
      })
    );

    await this.loadAllFiltered();
    await this.presentUsersUpdateListener.init();
  }

  async stopListening(): Promise<void> {
    unsubscribeAll(this.subscriptions);
    await this.presentUsersUpdateListener.stop();
    this.store.set([]);
  }

  async setFilters(filters: PresentDeviceUserFilters): Promise<void> {
    this.store.update({
      filters
    });
    await this.loadAllFiltered();
  }

  async forceInvalidate(userId: string): AsyncResult<boolean, ErrorInformation> {
    return await this.presentDeviceUserService.forceInvalidate(userId);
  }

  async forceInvalidateAll(): AsyncResult<boolean, ErrorInformation> {
    return await this.presentDeviceUserService.forceInvalidateAll();
  }

  async forceStop(userId: string): AsyncResult<boolean, ErrorInformation> {
    return await this.presentDeviceUserService.forceStop(userId);
  }

  async forceStopAll(): AsyncResult<boolean, ErrorInformation> {
    return await this.presentDeviceUserService.forceStopAll();
  }

  private async loadAllFiltered(): Promise<void> {
    const { filters } = this.store.getValue();
    const result = await this.presentDeviceUserService.getAll(filters);
    await result.ifSuccessAsync(async (pus) => {
      this.store.set(pus);
    });

    result.ifError(() => {
      this.store.set([]);
    });
  }
}
